import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
  deriveEstimateFormValuesFromEstimate,
  type EditableLineItem,
  type EstimateTemplateDefaults,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import type {
  CustomerApprovalMethod,
  EstimateBookingType,
  EstimateRecurringEndType,
  EstimateRecurringFrequency,
  EstimateRecurringIntervalUnit,
} from '@/types/estimate';
import { EMPTY_TERMS_SELECTION, type TermsSelection } from '@/lib/terms';

interface UseEstimateDraftStateArgs {
  expirationDays?: number | null;
  templateId?: string;
  setCustomerName: Dispatch<SetStateAction<string>>;
  setCustomerEmail: Dispatch<SetStateAction<string>>;
  setCustomerPhone: Dispatch<SetStateAction<string>>;
  setPrimaryContactName: Dispatch<SetStateAction<string>>;
  setPrimaryContactTitle: Dispatch<SetStateAction<string>>;
  setCustomerAddress: Dispatch<SetStateAction<string>>;
  setContactId?: Dispatch<SetStateAction<string | null>>;
  setLineItems: Dispatch<SetStateAction<EditableLineItem[]>>;
  /** Owned by the form, not this hook — a template can name a job type. */
  setJobTypeId?: Dispatch<SetStateAction<string>>;
}

export function useEstimateDraftState({
  expirationDays,
  templateId,
  setCustomerName,
  setCustomerEmail,
  setCustomerPhone,
  setPrimaryContactName,
  setPrimaryContactTitle,
  setCustomerAddress,
  setContactId,
  setLineItems,
  setJobTypeId,
}: UseEstimateDraftStateArgs) {
  const [estimateNumber, setEstimateNumber] = useState('');
  const [title, setTitle] = useState('');
  const [linkedLeadId, setLinkedLeadId] = useState('');
  const [source, setSource] = useState('MANUAL');
  const [leadSource, setLeadSource] = useState('');
  const [leadSourceId, setLeadSourceId] = useState('');
  const [salespersonId, setSalespersonId] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [notes, setNotes] = useState('');
  // The whole T&C selection (mode + pinned template + snapshot), not just text:
  // the form has to remember *why* these terms are here so it can offer
  // "default / a template / custom" instead of a bare textarea.
  const [terms, setTerms] = useState<TermsSelection>(EMPTY_TERMS_SELECTION);
  // Estimate Template Builder — the document template this estimate renders
  // through; null uses the tenant default.
  const [docTemplateId, setDocTemplateId] = useState<string | null>(null);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState<'$' | '%'>('$');
  const [discountReason, setDiscountReason] = useState('');
  const [promotionId, setPromotionId] = useState<string | null>(null);
  const [pricingMode, setPricingMode] = useState<'STANDARD' | 'TM_OVERRIDE'>('STANDARD');
  const [depositValue, setDepositValue] = useState(0);
  const [depositType, setDepositType] = useState<'$' | '%'>('$');
  const [depositRequired, setDepositRequired] = useState(false);
  const [bookingType, setBookingType] = useState<EstimateBookingType>('ONE_OFF');
  const [recurringFrequency, setRecurringFrequency] =
    useState<EstimateRecurringFrequency>('MONTHLY');
  const [recurringInterval, setRecurringInterval] = useState<number>(1);
  const [recurringIntervalUnit, setRecurringIntervalUnit] =
    useState<EstimateRecurringIntervalUnit>('MONTHS');
  const [recurringStartDate, setRecurringStartDate] = useState<string>('');
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState<number[]>([]);
  const [recurringEndType, setRecurringEndType] = useState<EstimateRecurringEndType>('INDEFINITE');
  const [recurringEndAfterCount, setRecurringEndAfterCount] = useState<number>(0);
  const [recurringEndOnDate, setRecurringEndOnDate] = useState<string>('');

  const defaultValidUntil = useMemo(() => {
    const days = expirationDays ?? 30;
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate.toISOString().split('T')[0];
  }, [expirationDays]);

  const [validUntil, setValidUntil] = useState(defaultValidUntil);
  const [customerApprovalMethod, setCustomerApprovalMethod] = useState<CustomerApprovalMethod | ''>(
    '',
  );
  const [internalNotes, setInternalNotes] = useState('');
  const [requireSignature, setRequireSignature] = useState(true);

  /**
   * Apply the document defaults a template carries.
   *
   * Deliberately NOT `applyDraftState`: that hydrates a whole estimate,
   * customer and all, which is right for an edit or a duplicate and wrong here
   * — picking a template would wipe the customer the operator had already
   * entered. This sets only the keys the template actually specifies, so a
   * template silent about deposits leaves the deposit fields as they are.
   */
  const applyTemplateDefaults = useCallback(
    (next: EstimateTemplateDefaults) => {
      if (next.jobTypeId !== undefined) setJobTypeId?.(next.jobTypeId);
      if (next.scopeOfWork !== undefined) setScopeOfWork(next.scopeOfWork);
      if (next.internalNotes !== undefined) setInternalNotes(next.internalNotes);
      if (next.validUntil !== undefined) setValidUntil(next.validUntil);
      if (next.requireSignature !== undefined) setRequireSignature(next.requireSignature);
      if (next.depositRequired !== undefined) setDepositRequired(next.depositRequired);
      if (next.depositType !== undefined) setDepositType(next.depositType);
      if (next.depositValue !== undefined) setDepositValue(next.depositValue);
      if (next.discountType !== undefined) setDiscountType(next.discountType);
      if (next.discountValue !== undefined) setDiscountValue(next.discountValue);
      if (next.discountReason !== undefined) setDiscountReason(next.discountReason);
      if (next.pricingMode !== undefined) setPricingMode(next.pricingMode);
      if (next.bookingType !== undefined) setBookingType(next.bookingType);
      if (next.recurringFrequency !== undefined) setRecurringFrequency(next.recurringFrequency);
      if (next.recurringInterval !== undefined) setRecurringInterval(next.recurringInterval);
      if (next.recurringIntervalUnit !== undefined) {
        setRecurringIntervalUnit(next.recurringIntervalUnit);
      }
      if (next.recurringStartDate !== undefined) setRecurringStartDate(next.recurringStartDate);
      if (next.recurringDaysOfWeek !== undefined) setRecurringDaysOfWeek(next.recurringDaysOfWeek);
      if (next.recurringEndType !== undefined) setRecurringEndType(next.recurringEndType);
      if (next.recurringEndAfterCount !== undefined) {
        setRecurringEndAfterCount(next.recurringEndAfterCount);
      }
      if (next.recurringEndOnDate !== undefined) setRecurringEndOnDate(next.recurringEndOnDate);
    },
    [setJobTypeId, setScopeOfWork],
  );

  const applyDraftState = useCallback(
    (next: ReturnType<typeof deriveEstimateFormValuesFromEstimate>) => {
      if (next.estimateNumber !== undefined) setEstimateNumber(next.estimateNumber);
      if (next.title !== undefined) setTitle(next.title);
      setContactId?.(next.contactId);
      setLinkedLeadId(next.linkedLeadId);
      // NOT-584 (A2) — no default here. This runs for BOTH a duplicate (where
      // MANUAL is a true answer, and is now supplied by the duplicate derive)
      // and an existing row being edited (where a blank attribution means the
      // row has none). Defaulting for both wrote a fabricated MANUAL into
      // `metadata.source` on the next save of every legacy estimate. A brand
      // new estimate never reaches here — its state already starts at MANUAL.
      setSource(next.source);
      setLeadSource(next.leadSource);
      setLeadSourceId(next.leadSourceId);
      setSalespersonId(next.salespersonId);
      setCustomerName(next.customerName);
      setCustomerEmail(next.customerEmail);
      setCustomerPhone(next.customerPhone);
      setPrimaryContactName(next.primaryContactName ?? '');
      setPrimaryContactTitle(next.primaryContactTitle ?? '');
      setCustomerAddress(next.customerAddress);
      setScopeOfWork(next.scopeOfWork ?? '');
      setNotes(next.notes);
      setTerms(next.terms);
      setDocTemplateId(next.docTemplateId ?? null);
      setTagIds(next.tagIds);
      setInternalNotes(next.internalNotes);
      if (next.validUntil) setValidUntil(next.validUntil);
      setDiscountValue(next.discountValue);
      setDiscountType(next.discountType);
      setDiscountReason(next.discountReason);
      setPromotionId(next.promotionId ?? null);
      setCustomerApprovalMethod(next.customerApprovalMethod);
      setRequireSignature(next.requireSignature);
      setPricingMode(next.pricingMode);
      setDepositValue(next.depositValue);
      setDepositType(next.depositType);
      setDepositRequired(next.depositRequired);
      if (next.bookingType) setBookingType(next.bookingType);
      if (next.recurringFrequency) setRecurringFrequency(next.recurringFrequency);
      if (next.recurringInterval) setRecurringInterval(next.recurringInterval);
      if (next.recurringIntervalUnit) setRecurringIntervalUnit(next.recurringIntervalUnit);
      if (next.recurringStartDate) setRecurringStartDate(next.recurringStartDate);
      if (next.recurringDaysOfWeek) setRecurringDaysOfWeek(next.recurringDaysOfWeek);
      if (next.recurringEndType) setRecurringEndType(next.recurringEndType);
      if (next.recurringEndAfterCount) setRecurringEndAfterCount(next.recurringEndAfterCount);
      if (next.recurringEndOnDate) setRecurringEndOnDate(next.recurringEndOnDate);
      setLineItems(next.lineItems);
    },
    [
      setCustomerName,
      setCustomerEmail,
      setCustomerPhone,
      setPrimaryContactName,
      setPrimaryContactTitle,
      setCustomerAddress,
      setContactId,
      setLineItems,
    ],
  );

  return {
    estimateNumber,
    setEstimateNumber,
    title,
    setTitle,
    linkedLeadId,
    setLinkedLeadId,
    source,
    setSource,
    leadSource,
    setLeadSource,
    leadSourceId,
    setLeadSourceId,
    salespersonId,
    setSalespersonId,
    tagIds,
    setTagIds,
    scopeOfWork,
    setScopeOfWork,
    notes,
    setNotes,
    terms,
    setTerms,
    docTemplateId,
    setDocTemplateId,
    discountValue,
    setDiscountValue,
    discountType,
    setDiscountType,
    discountReason,
    setDiscountReason,
    promotionId,
    setPromotionId,
    pricingMode,
    setPricingMode,
    depositValue,
    setDepositValue,
    depositType,
    setDepositType,
    depositRequired,
    setDepositRequired,
    bookingType,
    setBookingType,
    recurringFrequency,
    setRecurringFrequency,
    recurringInterval,
    setRecurringInterval,
    recurringIntervalUnit,
    setRecurringIntervalUnit,
    recurringStartDate,
    setRecurringStartDate,
    recurringDaysOfWeek,
    setRecurringDaysOfWeek,
    recurringEndType,
    setRecurringEndType,
    recurringEndAfterCount,
    setRecurringEndAfterCount,
    recurringEndOnDate,
    setRecurringEndOnDate,
    validUntil,
    setValidUntil,
    customerApprovalMethod,
    setCustomerApprovalMethod,
    internalNotes,
    setInternalNotes,
    requireSignature,
    setRequireSignature,
    selectedTemplateId: templateId ?? '',
    applyDraftState,
    applyTemplateDefaults,
  };
}
